import { D, dec, money, round2, ZERO } from './money.js';
/**
 * Dois modos:
 *
 *   preço sem IVA (omissão)            preço com IVA incluído
 *   base  = q × p × (1 − d)            total = q × p × (1 − d)
 *   IVA   = base × taxa                base  = total ÷ (1 + taxa)
 *   total = base + IVA                 IVA   = total − base   ← subtracção
 *
 * A subtracção é o ponto: um artigo a 100 MT c/IVA dá base 86,21, e
 * 86,21 × 1,16 = 100,0036. Arredondar o IVA por si dava 200,01 para q=2 —
 * a queixa real que deu origem a este modo no sistema Ribeiro. Pela
 * diferença, base + IVA = total por construção, para qualquer quantidade.
 */
export function calcLine(input) {
    const q = dec(input.quantity);
    const p = dec(input.unitPrice);
    const d = clampPct(input.discountPct);
    const rate = dec(input.taxRate);
    if (q.isNegative())
        throw new RangeError('Quantidade negativa');
    if (p.isNegative())
        throw new RangeError('Preço negativo');
    if (rate.isNegative())
        throw new RangeError('Taxa negativa');
    const gross = q.times(p);
    const afterDiscount = gross.times(new D(1).minus(d.div(100)));
    if (input.priceIncludesTax) {
        const total = round2(afterDiscount);
        const net = round2(total.div(new D(1).plus(rate.div(100))));
        const tax = total.minus(net);
        return {
            gross: money(gross),
            discountAmount: money(round2(gross).minus(total)),
            netAmount: money(net),
            taxAmount: money(tax),
            totalAmount: money(total),
        };
    }
    const net = round2(afterDiscount);
    const tax = round2(net.times(rate).div(100));
    return {
        gross: money(gross),
        discountAmount: money(round2(gross).minus(net)),
        netAmount: money(net),
        taxAmount: money(tax),
        totalAmount: money(net.plus(tax)),
    };
}
/**
 * Totais do documento. O desconto global aplica-se a cada GRUPO DE TAXA e só
 * depois se arredonda, por grupo — assim o quadro-resumo por taxa impresso no
 * documento soma exactamente à base e ao IVA totais, sem cêntimos órfãos.
 */
export function calcDocument(input) {
    const lines = input.lines.map(calcLine);
    const factor = new D(1).minus(clampPct(input.globalDiscountPct).div(100));
    const groups = new Map();
    let linesNet = ZERO;
    input.lines.forEach((l, i) => {
        const r = lines[i];
        const key = dec(l.taxRate).toFixed(2);
        const g = groups.get(key) ?? { net: ZERO, tax: ZERO };
        g.net = g.net.plus(r.netAmount);
        g.tax = g.tax.plus(r.taxAmount);
        groups.set(key, g);
        linesNet = linesNet.plus(r.netAmount);
    });
    const summary = new Map();
    let linesNetAfterDiscount = ZERO;
    for (const [rate, g] of groups) {
        const net = round2(g.net.times(factor));
        const tax = round2(g.tax.times(factor));
        summary.set(rate, { net, tax });
        linesNetAfterDiscount = linesNetAfterDiscount.plus(net);
    }
    const shipping = round2(dec(input.shippingAmount));
    if (shipping.isNegative())
        throw new RangeError('Transporte negativo');
    if (shipping.greaterThan(0)) {
        const rate = dec(input.shippingTaxRate ?? 0);
        const key = rate.toFixed(2);
        const s = summary.get(key) ?? { net: ZERO, tax: ZERO };
        s.net = s.net.plus(shipping);
        s.tax = s.tax.plus(round2(shipping.times(rate).div(100)));
        summary.set(key, s);
    }
    let netTotal = ZERO;
    let taxTotal = ZERO;
    const taxSummary = [...summary.entries()]
        .sort((a, b) => new D(b[0]).comparedTo(new D(a[0])))
        .map(([rate, s]) => {
        netTotal = netTotal.plus(s.net);
        taxTotal = taxTotal.plus(s.tax);
        return { rate, netAmount: money(s.net), taxAmount: money(s.tax) };
    });
    const grossTotal = netTotal.plus(taxTotal);
    const withholding = round2(linesNetAfterDiscount.times(dec(input.withholdingPct ?? 0)).div(100));
    return {
        lines,
        linesNet: money(linesNet),
        globalDiscountAmount: money(round2(linesNet).minus(linesNetAfterDiscount)),
        shippingAmount: money(shipping),
        netTotal: money(netTotal),
        taxTotal: money(taxTotal),
        grossTotal: money(grossTotal),
        withholdingAmount: money(withholding),
        payableTotal: money(grossTotal.minus(withholding)),
        taxSummary,
    };
}
/** Converte um preço guardado para base tributável (comparar preços, margem). */
export function priceExcludingTax(unitPrice, taxRate, includesTax) {
    return includesTax ? money(dec(unitPrice).div(new D(1).plus(dec(taxRate).div(100)))) : money(unitPrice);
}
/** O inverso: o que o cliente final vê. */
export function priceIncludingTax(unitPrice, taxRate, includesTax) {
    return includesTax ? money(unitPrice) : money(dec(unitPrice).times(new D(1).plus(dec(taxRate).div(100))));
}
function clampPct(v) {
    const d = dec(v ?? 0);
    if (d.isNegative() || d.greaterThan(100))
        throw new RangeError('Percentagem fora de 0–100');
    return d;
}
//# sourceMappingURL=calc.js.map